// Detecta capacidade do dispositivo e devolve um "tier" usado pra dimensionar
// pool de workers, cleanup e qualidade default das tools.
//
// Ordem de precedência:
//   1. URL ?tier=low|mid|high  (pra emular em testes)
//   2. localStorage.wuwei_tier  (override persistente)
//   3. Auto-detect via navigator.deviceMemory + hardwareConcurrency
//
// Nada bloqueante — todas as APIs são síncronas/já disponíveis no boot.

export type DeviceTier = "low" | "mid" | "high";

export interface DeviceCapabilities {
  tier: DeviceTier;
  /** Máximo de workers ativos simultaneamente. Demais ficam em queue ou matam LRU. */
  maxActiveWorkers: number;
  /** Idle ms até auto-kill de worker em low/mid (libera RAM). 0 = não mata. */
  idleKillMs: number;
  /** Cores reportados pelo browser (-1 se desconhecido). */
  cores: number;
  /** RAM aproximada em GB (-1 se desconhecido — Firefox/Safari não expõem). */
  ramGb: number;
  /** True se isMobile. */
  isMobile: boolean;
  /** Origem da decisão. */
  source: "url" | "localStorage" | "auto";
}

const STORAGE_KEY = "wuwei_tier";

function readUrlTier(): DeviceTier | null {
  try {
    const u = new URL(location.href);
    const t = u.searchParams.get("tier");
    if (t === "low" || t === "mid" || t === "high") return t;
  } catch {}
  return null;
}

function readStorageTier(): DeviceTier | null {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t === "low" || t === "mid" || t === "high") return t;
  } catch {}
  return null;
}

function isMobileUA(): boolean {
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
}

function autoDetectTier(): { tier: DeviceTier; cores: number; ramGb: number; isMobile: boolean } {
  const cores = (navigator.hardwareConcurrency as number | undefined) ?? -1;
  const ramGb = ((navigator as unknown as { deviceMemory?: number }).deviceMemory) ?? -1;
  const mobile = isMobileUA();

  // Política:
  // - low:  mobile sempre (a não ser flagship com 8+ GB), ou < 4 GB / < 4 cores
  // - high: desktop com >= 8 GB E >= 8 cores
  // - mid:  resto

  if (mobile && (ramGb < 0 || ramGb < 6)) return { tier: "low", cores, ramGb, isMobile: mobile };
  if (ramGb > 0 && ramGb < 4) return { tier: "low", cores, ramGb, isMobile: mobile };
  if (cores > 0 && cores < 4) return { tier: "low", cores, ramGb, isMobile: mobile };

  if (!mobile && ramGb >= 8 && cores >= 8) return { tier: "high", cores, ramGb, isMobile: mobile };

  return { tier: "mid", cores, ramGb, isMobile: mobile };
}

function tierToCapabilities(tier: DeviceTier): { maxActiveWorkers: number; idleKillMs: number } {
  switch (tier) {
    case "low":  return { maxActiveWorkers: 2,  idleKillMs: 45_000 };
    case "mid":  return { maxActiveWorkers: 4,  idleKillMs: 120_000 };
    case "high": return { maxActiveWorkers: 8,  idleKillMs: 0 };  // sem cleanup
  }
}

let cached: DeviceCapabilities | null = null;

export function detectDevice(): DeviceCapabilities {
  if (cached) return cached;

  const fromUrl = readUrlTier();
  if (fromUrl) {
    const auto = autoDetectTier();
    const cap = tierToCapabilities(fromUrl);
    cached = {
      tier: fromUrl,
      ...cap,
      cores: auto.cores,
      ramGb: auto.ramGb,
      isMobile: auto.isMobile,
      source: "url",
    };
    return cached;
  }

  const fromStorage = readStorageTier();
  if (fromStorage) {
    const auto = autoDetectTier();
    const cap = tierToCapabilities(fromStorage);
    cached = {
      tier: fromStorage,
      ...cap,
      cores: auto.cores,
      ramGb: auto.ramGb,
      isMobile: auto.isMobile,
      source: "localStorage",
    };
    return cached;
  }

  const auto = autoDetectTier();
  const cap = tierToCapabilities(auto.tier);
  cached = {
    ...auto,
    ...cap,
    source: "auto",
  };
  return cached;
}

/** Persiste override (chamado pelo /tier <low|mid|high>). null limpa. */
export function setTierOverride(tier: DeviceTier | null): void {
  try {
    if (tier == null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, tier);
  } catch {}
  cached = null; // força re-detect na próxima call
}

/** Label human-readable. */
export function tierLabel(d: DeviceCapabilities): string {
  const ram = d.ramGb > 0 ? `${d.ramGb}GB` : "?GB";
  const cores = d.cores > 0 ? `${d.cores}c` : "?c";
  const mob = d.isMobile ? "mobile" : "desktop";
  const src = d.source === "auto" ? "auto" : `via ${d.source}`;
  return `tier=${d.tier} (${mob}, ${cores}, ${ram}; ${src}) → max ${d.maxActiveWorkers} workers, idle-kill ${d.idleKillMs}ms`;
}
